/** Focus trail and pocket level rules for the memory frame. */
import { YOU } from "@/api/memory-focus";

export type FocusMove = "push" | "jump";
export type PocketLevel =
  | "search"
  | "thing"
  | "fact"
  | "suggestion"
  | "bundle"
  | "sources"
  | "history";
export interface PocketEntry {
  level: PocketLevel;
  id: string;
}
export type MemorySelection = {
  type: "thing" | "fact" | "suggestion" | "bundle";
  id: string;
} | null;
export const POCKET_ROOT: PocketEntry = { level: "search", id: "" };
export const FOCUS_BOUND: ReadonlySet<PocketLevel> = new Set([
  "thing",
  "fact",
  "bundle",
  "sources",
  "history",
]);

/** A jump restarts at You; a push appends or steps back to an existing thing. */
export function nextTrail(
  trail: readonly string[],
  id: string,
  move: FocusMove,
): string[] {
  if (move === "jump") return id === YOU ? [YOU] : [YOU, id];
  const index = trail.indexOf(id);
  return index >= 0 ? trail.slice(0, index + 1) : [...trail, id];
}

export function followFocus(
  stack: readonly PocketEntry[],
  focusId: string,
): PocketEntry[] {
  const top = stack[stack.length - 1];
  if (
    !top ||
    !FOCUS_BOUND.has(top.level) ||
    (top.level === "thing" && top.id === focusId)
  )
    return [...stack];
  return [POCKET_ROOT, { level: "thing", id: focusId }];
}

export function selectionOf(entry: PocketEntry | undefined): MemorySelection {
  if (!entry || entry.level === "search") return null;
  return entry.level === "sources" || entry.level === "history"
    ? { type: "fact", id: entry.id }
    : { type: entry.level, id: entry.id };
}

/** The store fields that always change together with the pocket stack. */
export function pocketState(stack: PocketEntry[]): {
  pocketStack: PocketEntry[];
  selection: MemorySelection;
  pocketLevel: PocketLevel | null;
} {
  const top = stack[stack.length - 1];
  return {
    pocketStack: stack,
    selection: selectionOf(top),
    pocketLevel: top?.level ?? null,
  };
}
